import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api as axios } from '../utils/apiClient';
import './SiteMonitor.css';

const SiteMonitor = ({ site: siteProp, initialData = null, initialStatus = 'unavailable', initialStatusDetails = null }) => {
  const { siteId: siteIdFromParams } = useParams();
  // Use site prop if provided, otherwise use siteId from params
  const siteId = siteProp?._id || siteIdFromParams;
  const [monitorData, setMonitorData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [monitorStatus, setMonitorStatus] = useState(initialStatus || siteProp?.monitorStatus?.status || null);
  const [statusDetails, setStatusDetails] = useState(initialStatusDetails || siteProp?.monitorStatus?.status_details || null);
  const [showDetails, setShowDetails] = useState({
    plugins: false,
    themes: false,
    health: false
  });

  useEffect(() => {
    if (siteId) {
      fetchMonitorData(true);
    }
  }, [siteId]);

  useEffect(() => {
    if (initialData) {
      setMonitorData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (initialStatus) {
      setMonitorStatus(initialStatus);
    }
    if (initialStatusDetails) {
      setStatusDetails(initialStatusDetails);
    }
  }, [initialStatus, initialStatusDetails]);

  const fetchMonitorData = async (force = false) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[SiteMonitor] Fetching monitor data', { siteId, force });
      const response = await axios.get(`/api/sites/${siteId}/monitor${force ? '?force=true' : ''}`);
      console.log('[SiteMonitor] Monitor response', response.data);
      
      // Handle response format from backend
      if (response.data.success === false) {
        setError(response.data.error || 'Failed to load monitoring data');
        setMonitorData(null);
        setMonitorStatus(response.data.status || 'unavailable');
        setStatusDetails(response.data.status_details || null);
      } else {
        // Backend returns { success: true, data: {...} } or just the data
        setMonitorData(response.data.data || response.data);
        setMonitorStatus(response.data.status || 'live');
        setStatusDetails(response.data.status_details || null);
      }
    } catch (err) {
      console.error('Error fetching monitor data:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load monitoring data');
      setMonitorData(null);
      setMonitorStatus('unavailable');
      setStatusDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const syncMonitorData = async () => {
    try {
      setSyncing(true);
      setError(null);
      console.log('[SiteMonitor] Syncing monitor data', { siteId });
      const response = await axios.post(`/api/sites/${siteId}/sync-monitor`);
      
      // Handle response format
      if (response.data.success === false) {
        setError(response.data.error || 'Failed to sync monitoring data');
        setMonitorStatus(response.data.status || 'unavailable');
        setStatusDetails(response.data.status_details || null);
      } else {
        // Backend returns { success: true, data: {...} }
        setMonitorData(response.data.data || response.data);
        setMonitorStatus(response.data.status || 'live');
        setStatusDetails(response.data.status_details || null);
      }
    } catch (err) {
      console.error('Error syncing monitor data:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to sync monitoring data');
      setMonitorStatus('unavailable');
      setStatusDetails(null);
    } finally {
      setSyncing(false);
    }
  };

  const getPerformanceColor = (score) => {
    if (score === null || score === undefined) return '#999';
    if (score >= 90) return '#28a745';
    if (score >= 50) return '#ffc107';
    return '#dc3545';
  };

  const getHealthStatus = (health) => {
    if (!health || !health.available) {
      return { status: 'unknown', text: 'N/A', color: '#999' };
    }
    const critical = health.critical_issues_count || 0;
    const recommended = health.recommended_count || 0;
    
    if (critical > 0) {
      return { status: 'critical', text: `${critical} critical issues`, color: '#dc3545' };
    }
    if (recommended > 0) {
      return { status: 'warning', text: `${recommended} recommended`, color: '#ffc107' };
    }
    return { status: 'good', text: 'Good', color: '#28a745' };
  };

  if (loading) {
    return (
      <div className="site-monitor">
        <div className="loading">Loading monitoring data...</div>
      </div>
    );
  }

  if (error && !monitorData) {
    return (
      <div className="site-monitor">
        <div className="error-message">
          <h3>⚠️ Error</h3>
          <p>{error}</p>
          <button onClick={() => fetchMonitorData(true)} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  const data = monitorData;
  const pluginsList = data?.plugins || [];
  const pluginCount = pluginsList.length || data?.plugins_count || 0;
  const healthStatus = data?.site_health ? getHealthStatus(data.site_health) : null;
  const status = monitorStatus || 'unavailable';
  const tooltipParts = [
    `Status: ${status.toUpperCase()}`,
    `Last updated: ${statusDetails?.last_updated ? new Date(statusDetails.last_updated).toLocaleString() : 'N/A'}`,
  ];
  if (statusDetails?.succeeded_endpoints?.length) {
    tooltipParts.push('✔ ' + statusDetails.succeeded_endpoints.join('\n✔ '));
  }
  if (statusDetails?.failed_endpoints?.length) {
    tooltipParts.push('✖ ' + statusDetails.failed_endpoints.join('\n✖ '));
  }
  const statusTooltip = tooltipParts.filter(Boolean).join('\n');

  return (
    <div className="site-monitor">
      <div className="monitor-header">
        <div className="monitor-title">
          <h2>Site Overview</h2>
          {status === 'live' && (
            <span className="status-badge status-live" title={statusTooltip}>
              LIVE
            </span>
          )}
        </div>
        <div className="header-actions">
          <button 
            onClick={() => fetchMonitorData(true)} 
            className="btn-refresh"
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <button 
            onClick={syncMonitorData} 
            className="btn-sync"
            disabled={syncing}
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync Now'}
          </button>
        </div>
      </div>

      {data && (
        <div className="monitor-grid">
          {/* Basic Info Card */}
          <div className="monitor-card">
            <h3>Basic Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">WordPress Version</span>
                <span className="info-value">{data.wp_version || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">PHP Version</span>
                <span className="info-value">{data.php_version || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Active Theme</span>
                <span className="info-value">{data.active_theme?.name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Theme Version</span>
                <span className="info-value">{data.active_theme?.version || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Plugins Card */}
          <div className="monitor-card">
            <div className="card-header">
              <h3>Plugins ({pluginCount})</h3>
              <button 
                onClick={() => setShowDetails({ ...showDetails, plugins: !showDetails.plugins })}
                className="btn-toggle"
              >
                {showDetails.plugins ? '▼' : '▶'}
              </button>
            </div>
            <div className="card-summary">
              Active: {pluginsList.filter(p => p.active).length} / {pluginCount}
            </div>
            {showDetails.plugins && pluginsList.length > 0 ? (
              <div className="plugins-list">
                {pluginsList.map((plugin, idx) => (
                  <div key={idx} className={`plugin-item ${plugin.active ? 'active' : ''}`}>
                    <div className="plugin-name">{plugin.name}</div>
                    <div className="plugin-meta">
                      <span>v{plugin.version}</span>
                      <span className={`badge ${plugin.active ? 'badge-active' : 'badge-inactive'}`}>
                        {plugin.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : showDetails.plugins && pluginCount > 0 ? (
              <p>Plugin list not provided by endpoint (count {pluginCount}).</p>
            ) : null}
          </div>

          {/* Themes Card */}
          <div className="monitor-card">
            <div className="card-header">
              <h3>Themes ({data.themes?.length || 0})</h3>
              <button 
                onClick={() => setShowDetails({ ...showDetails, themes: !showDetails.themes })}
                className="btn-toggle"
              >
                {showDetails.themes ? '▼' : '▶'}
              </button>
            </div>
            {showDetails.themes && data.themes && (
              <div className="themes-list">
                {data.themes.map((theme, idx) => (
                  <div key={idx} className={`theme-item ${theme.active ? 'active' : ''}`}>
                    <div className="theme-name">{theme.name}</div>
                    <div className="theme-meta">
                      <span>v{theme.version}</span>
                      <span className={`badge ${theme.active ? 'badge-active' : 'badge-inactive'}`}>
                        {theme.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Site Health Card */}
          <div className="monitor-card">
            <div className="card-header">
              <h3>Site Health</h3>
              <button 
                onClick={() => setShowDetails({ ...showDetails, health: !showDetails.health })}
                className="btn-toggle"
              >
                {showDetails.health ? '▼' : '▶'}
              </button>
            </div>
            {data.site_health && (
              <>
                <div className="health-summary">
                  <div className="health-status" style={{ color: healthStatus?.color }}>
                    {healthStatus?.text}
                  </div>
                  {data.site_health.summary && (
                    <div className="health-stats">
                      <span>Good: {data.site_health.summary.good || 0}</span>
                      <span>Recommended: {data.site_health.summary.recommended || 0}</span>
                      <span>Critical: {data.site_health.summary.critical || 0}</span>
                    </div>
                  )}
                </div>
                {showDetails.health && data.site_health.critical_issues && data.site_health.critical_issues.length > 0 && (
                  <div className="health-issues">
                    <h4>Critical Issues:</h4>
                    <ul>
                      {data.site_health.critical_issues.map((issue, idx) => (
                        <li key={idx}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Performance Card */}
          <div className="monitor-card">
            <h3>Performance Score</h3>
            <div className="performance-display">
              {data.performance_score !== null && data.performance_score !== undefined ? (
                <>
                  <div 
                    className="performance-score"
                    style={{ color: getPerformanceColor(data.performance_score) }}
                  >
                    {data.performance_score}/100
                  </div>
                  <div className="performance-bar">
                    <div 
                      className="performance-fill"
                      style={{ 
                        width: `${data.performance_score}%`,
                        backgroundColor: getPerformanceColor(data.performance_score)
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="performance-na">Not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {data?.timestamp && (
        <div className="monitor-footer">
          Last updated: {new Date(data.timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default SiteMonitor;

